/**
 * fichaPricing.js
 * ----------------------------------------------------------------------------
 * FONTE ÚNICA da fórmula de preço dos itens da ficha_tecnica.
 *
 * Extraída de ModalAddProduct.recalculateItemTotal para que o editor de
 * desenho técnico (redimensionar peça / editar cota) recalcule EXATAMENTE
 * como o caixa calcula. Qualquer mudança de regra financeira acontece aqui,
 * uma vez só.
 *
 * Regras (idênticas ao modal):
 *  - Peça (Principal/Secundária) com largura <= 0.1m  => cobrada em ML:
 *      calculo = quantidade * comprimento * (10% do preço da placa)
 *  - Peça com largura > 0.1m                          => cobrada em M²:
 *      calculo = quantidade * comprimento * largura * preço da placa
 *  - Acabamento                                       => unidade:
 *      calculo = preço do insumo; total multiplica pela quantidade
 *  - taxa = (calculo / 100) * taxFinishing  (lucro/acabamento percentual)
 *  - total é STRING com 2 casas (padrão já persistido pelo modal)
 */

import { roundMeasure } from "@/apps/shared/utils/measurements";

export function toNumber(value) {
  if (typeof value === "number") return value;
  if (value === null || value === undefined || value === "") return 0;
  return parseFloat(`${value}`.replace(",", ".")) || 0;
}

export function normalizeType(type) {
  return `${type || ""}`
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export function isStonePieceType(type) {
  const normalized = normalizeType(type);
  return (
    normalized.includes("peca principal") ||
    normalized.includes("peca secundaria")
  );
}

export function isFinishingType(type) {
  return normalizeType(type).includes("acabamento");
}

/**
 * Recalcula preco/calculo/taxa/total de UM item da ficha.
 *
 * @param {object} item item da ficha_tecnica (não é mutado)
 * @param {object} opts
 *  - platePrice: preço da placa (usado por peças M²/ML)
 *  - taxFinishing: % de lucro/acabamento
 *  - resolveFinishingPrice: fallback opcional (item) => preço, usado quando
 *    um item Acabamento não tem preço salvo (o modal busca na lista de
 *    insumos pelo nome)
 * @returns {object} novo item com os campos financeiros atualizados
 */
export function recalculateItem(item, opts = {}) {
  const platePrice = toNumber(opts.platePrice);
  const lucro_percentual = toNumber(opts.taxFinishing);
  const quantidade = parseInt(item.quantity) || 0;

  if (isFinishingType(item.type)) {
    let preco = toNumber(item.preco || item.ultimo_preco_de_compra);
    if (!preco && typeof opts.resolveFinishingPrice === "function") {
      preco = toNumber(opts.resolveFinishingPrice(item));
    }
    const calculo = preco;
    const taxa = (calculo / 100) * lucro_percentual;
    return {
      ...item,
      preco,
      calculo,
      taxa,
      total: parseFloat(quantidade * (taxa + calculo)).toFixed(2)
    };
  }

  if (isStonePieceType(item.type)) {
    const comprimento = roundMeasure(item.comprimento);
    const largura = roundMeasure(item.largura);
    let calculo;
    if (largura <= 0.1) {
      const dezporcento = (platePrice / 100) * 10;
      calculo = quantidade * comprimento * dezporcento;
    } else {
      calculo = quantidade * comprimento * largura * platePrice;
    }
    const taxa = (calculo / 100) * lucro_percentual;
    return {
      ...item,
      comprimento,
      largura,
      preco: platePrice,
      calculo,
      taxa,
      total: parseFloat(taxa + calculo).toFixed(2)
    };
  }

  return { ...item };
}

/**
 * Deriva a % de taxFinishing efetivamente usada quando o item foi criado
 * (taxa / calculo * 100). Permite recalcular no editor da venda sem depender
 * da config atual do caixa — que pode ter mudado desde o orçamento.
 */
export function deriveTaxFinishing(item, fallback = 30) {
  const calculo = toNumber(item && item.calculo);
  const taxa = toNumber(item && item.taxa);
  if (calculo > 0 && taxa >= 0) {
    return (taxa / calculo) * 100;
  }
  return fallback;
}
